import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router";
import { btn } from "@/components/actions";
import { checkpoints, type LocalCheckpoint } from "@/lib/recovery/store";
import { RecoveryContext } from "@/lib/recovery/context";
import { randomUuid } from "@/lib/uuid";
import './recovery.css';

type Field = { identity: string; value: string; checked: boolean; type: string };
type Draft = { actor_id: string; fields: Field[]; atoms: Record<string, unknown>; localFiles: string[]; scrollY: number; route: string };
type Control = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
const recoverySaveDelayMs = 600;
const sensitive = /password|secret|token|credential|api.?key|private.?key|authorization/i;
function controls(root: HTMLElement): [string, Control][] {
  const counts = new Map<string, number>();
  return [...root.querySelectorAll<Control>("input,textarea,select")].filter(el =>
    !el.disabled && !("readOnly" in el && el.readOnly) && !["password", "file", "hidden", "submit", "button"].includes(el.type)
    && !el.closest('[data-recovery-exclude]') && !/ack|decision|confirm/i.test(el.name)
    && !sensitive.test([el.name, el.getAttribute('aria-label'), el.getAttribute('placeholder'), el.labels?.[0]?.textContent].join(' ')))
    .map(el => {
      const group = el.closest('[data-recovery-scope],[data-node-id],[data-resource-id],[data-testid]');
      const container = group?.getAttribute("data-recovery-scope") || group?.getAttribute("data-node-id") || group?.getAttribute("data-resource-id") || group?.getAttribute("data-testid") || "";
      const label = el.labels?.[0]?.textContent?.trim().slice(0, 160) || "";
      const base = `${container}:${el.tagName}:${el.type}:${el.name || el.getAttribute("aria-label") || label || el.getAttribute("placeholder") || "field"}`;
      const n = counts.get(base) || 0; counts.set(base, n + 1);
      return [`${base}:${n}`, el];
    });
}
function safe(value: unknown) {
  const raw = JSON.stringify(value);
  if (raw.length > 2 * 1024 * 1024) throw new Error("Working form is too large for local recovery");
  function check(item: unknown): void {
    if (typeof item === 'string' && /(?:password|secret|access_token|refresh_token|authorization|api_key|private_key)["']?\s*[:=]/i.test(item))
      throw new Error('Credentials are excluded from local recovery');
    if (item && typeof item === 'object') for (const [key, child] of Object.entries(item)) {
      if (sensitive.test(key)) throw new Error('Credentials are excluded from local recovery');
      check(child);
    }
  }
  check(value);
  return JSON.parse(raw) as Draft;
}

/** Local form/selection recovery; server-owned operations retain their own replay identity. */
export function PortalRecovery({ actorId, children }: { actorId: string; children: ReactNode }) {
  const location = useLocation(), root = useRef<HTMLDivElement>(null);
  const route = location.pathname + location.search, scope = `portal:${actorId}:${route}`;
  const writer = useMemo(() => randomUuid(), [scope]);
  const atoms = useRef(new Map<string, { read: () => unknown; restore: (value: unknown) => void }>());
  const [available, setAvailable] = useState<LocalCheckpoint<Draft> | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const sequence = useRef(0), liveKey = useRef(`${scope}:${writer}`);
  liveKey.current = `${scope}:${writer}`;
  const capture = useCallback(async () => {
    if (!root.current) return;
    const key = `${scope}:${writer}`;
    try {
      const fields = controls(root.current).map(([identity, el]) => ({ identity, value: el.value,
        checked: el instanceof HTMLInputElement ? el.checked : false, type: el.type }));
      const values = Object.fromEntries([...atoms.current].map(([name, value]) => [name, value.read()]));
      const localFiles = [...root.current.querySelectorAll<HTMLInputElement>('input[type=file]')].flatMap(el => [...(el.files || [])].map(f => f.name));
      const payload = safe({ actor_id: actorId, route, fields, atoms: values, localFiles, scrollY: window.scrollY });
      const n = ++sequence.current;
      await checkpoints.save({ key, scope, sequence: n, payload });
      if (liveKey.current === key && sequence.current === n) setStatus(localFiles.length ? "Working form saved on this device — reselect local files after reload" : null);
    } catch (e) { if (liveKey.current === key) setStatus(e instanceof Error ? e.message : "Local save unavailable"); }
  }, [scope, writer, actorId, route]);
  // Typing and recoverable-state changes only mark the form dirty; one save runs after the burst.
  const captureRef = useRef(capture), dirty = useRef(false), timer = useRef<number | undefined>(undefined);
  captureRef.current = capture;
  const flush = useCallback(() => {
    window.clearTimeout(timer.current); timer.current = undefined;
    if (!dirty.current) return;
    dirty.current = false;
    void captureRef.current();
  }, []);
  const changed = useCallback((event?: Event) => {
    const target = event?.target;
    if (target instanceof Element && target.closest("[data-recovery-exclude]")) return;
    dirty.current = true;
    if (timer.current === undefined) timer.current = window.setTimeout(flush, recoverySaveDelayMs);
  }, [flush]);
  const registry = useMemo(() => ({ changed, register(key: string, atom: { read: () => unknown; restore: (value: unknown) => void }) {
    atoms.current.set(key, atom);
    return () => { if (atoms.current.get(key) === atom) atoms.current.delete(key); };
  } }), [changed]);
  useEffect(() => {
    let active = true;
    sequence.current = 0; dirty.current = false; setAvailable(null); setStatus(null);
    checkpoints.list<Draft>(scope, 20).then(rows => {
      const previous = rows.find(x => x.key !== `${scope}:${writer}` && x.payload.actor_id === actorId && x.payload.route === route);
      if (active && previous) setAvailable(previous);
    }).catch(() => {});
    const current = root.current;
    current?.addEventListener("input", changed); current?.addEventListener("change", changed);
    // Save a pending change before a click or history navigation can unmount the form.
    const hide = () => { if (document.visibilityState === "hidden") flush(); };
    current?.addEventListener("click", flush, true);
    document.addEventListener("visibilitychange", hide);
    window.addEventListener("pagehide", flush);
    window.addEventListener("popstate", flush);
    return () => { active = false; current?.removeEventListener("input", changed);
      current?.removeEventListener("change", changed); current?.removeEventListener("click", flush, true);
      document.removeEventListener("visibilitychange", hide);
      window.removeEventListener("pagehide", flush); window.removeEventListener("popstate", flush);
      // Unmounted in place (same URL) the form is still the one that changed; after a route
      // change the DOM already belongs to the next route, so the click/popstate flush covered it.
      if (route === window.location.pathname + window.location.search) flush();
      else { window.clearTimeout(timer.current); timer.current = undefined; dirty.current = false; } };
  }, [scope, actorId, route, writer, changed, flush]);
  function restore() {
    if (!available || !root.current || available.payload.actor_id !== actorId || available.payload.route !== route) return;
    const fields = new Map(available.payload.fields.map(f => [f.identity, f]));
    for (const [identity, el] of controls(root.current)) {
      const saved = fields.get(identity);
      if (!saved || saved.type !== el.type) continue;
      if (el instanceof HTMLInputElement && ["checkbox", "radio"].includes(el.type)) {
        if (el.checked !== saved.checked) el.click();
      } else {
        const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : el instanceof HTMLSelectElement ? HTMLSelectElement.prototype : HTMLInputElement.prototype;
        Object.getOwnPropertyDescriptor(proto, "value")?.set?.call(el, saved.value);
        el.dispatchEvent(new Event(el instanceof HTMLSelectElement ? "change" : "input", { bubbles: true }));
      }
    }
    for (const [name, value] of atoms.current) if (Object.hasOwn(available.payload.atoms, name)) value.restore(available.payload.atoms[name]);
    window.scrollTo({ top: available.payload.scrollY });
    setAvailable(null); setStatus(available.payload.localFiles?.length
      ? "Restored working form — reselect local files, then review before submitting"
      : "Restored working form — review values before saving or submitting");
  }
  return <RecoveryContext.Provider value={registry}><div ref={root} className="portal-working-recovery">
    {(available || status) && <div className="working-recovery-status" role="status" data-testid="portal-recovery" data-recovery-exclude>
      {available ? <><span>Saved working form from {new Date(available.updatedAt || 0).toLocaleString()}</span>
        <button type="button" className={btn("plain")} onClick={restore}>Restore form</button>
        <button type="button" className={btn("plain")} onClick={() => { void checkpoints.remove(available.key); setAvailable(null); }}>Discard saved form</button></>
        : <span>{status}</span>}
    </div>}
    {children}
  </div></RecoveryContext.Provider>;
}
